<template>
    <!-- ローディング -->
    <LoadingIcon :is-loading="isLoading"></LoadingIcon>

    <h2>{{ $t('UserPlanView.plan_detail') }}</h2>
    <div class="mb-3 w-25">
        <select class="form-select shadow-sm" aria-label="Default select example" v-model="selectedMonth">
            <option v-for="(item, index) in monthList" :key="index" :value="item" @click="getMonthMessages(item)">
                {{ formatDateString(item) }}
            </option>
        </select>
    </div>
    <div class="mb-3 w-50">
        <table class="table table-bordered">
            <thead class="table-secondary">
                <tr>
                    <th scope="col">{{ $t('UserPlanView.model') }}</th>
                    <th scope="col">{{ $t('UserPlanView.points_per_message') }}</th>
                    <th scope="col">{{ $t('UserPlanView.messages_total') }}</th>
                    <th scope="col">{{ $t('UserPlanView.total_points') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="model in MonthPoint.models" :key="model.model_name">
                    <td>{{ model.model_name }}</td>
                    <td>{{ model.points_per_message }} {{ $t('UserPlanView.points_unit') }}</td>
                    <td>{{ model.messages_total }}</td>
                    <td>{{ model.total_points }}</td>
                </tr>
                <tr>
                    <td>{{ $t('UserPlanView.count') }}</td>
                    <td></td>
                    <td>{{ MonthPoint.messages_total }}</td>
                    <td>{{ MonthPoint.total_points }}</td>
                </tr>
            </tbody>
        </table>
    </div>
    <br>
    <h2>{{ $t('UserPlanView.resource_detail') }}</h2>
    <div class="mb-3 w-50">
        <table class="table table-bordered">
            <thead class="table-secondary">
                <tr>
                    <th scope="col">{{ $t('UserPlanView.element') }}</th>
                    <th scope="col">{{ $t('UserPlanView.usage_amount') }}</th>
                    <th scope="col">{{ $t('UserPlanView.limit') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{{ $t('UserPlanView.resource_characters_count') }}</td>
                    <td>{{ ResCount.resource }}</td>
                    <td>{{ ResCount.resource_limit }}</td>
                </tr>
                <tr>
                    <td>{{ $t('UserPlanView.user_count') }}</td>
                    <td>{{ ResCount.user }}</td>
                    <td>{{ ResCount.user_limit }}</td>
                </tr>
                <tr>
                    <td>{{ $t('UserPlanView.chat_count') }}</td>
                    <td>{{ ResCount.chat }}</td>
                    <td>{{ ResCount.chat_limit }}</td>
                </tr>
            </tbody>
        </table>
    </div>
    <br>
    <h2>{{ $t('UserPlanView.cost') }}</h2>
    <div class="mb-3 w-50">
        <table class="table table-bordered">
            <thead class="table-secondary">
                <tr>
                    <th scope="col">{{ $t('UserPlanView.element') }}</th>
                    <th scope="col">{{ $t('UserPlanView.amount') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{{ $t('UserPlanView.total_amount') }}</td>
                    <td>{{ MonthPoint.total_amount }}円</td>
                </tr>
            </tbody>
        </table>
    </div>
    <br>
    <div class="d-grid gap-2 d-md-flex">
        <button class="btn btn-primary" @click="downloadCSV">{{ $t('UserPlanView.csv') }}</button>
        <button class="btn btn-primary" @click="downloadCSVByUser">{{ $t('UserPlanView.csv_by_user') }}</button>
        <button class="btn btn-primary" @click="handleReturn">{{ $t('UserPlanView.handle_return') }}</button>
    </div>
    <br>
    <br>


    <!-- ポイント料金表 -->
    <h2>{{ $t('UserPlanView.point_fee_table') }}</h2>
    <div class="mb-3 w-50">
        <table class="table table-bordered">
            <thead class="table-secondary">
                <tr>
                    <th scope="col">{{ $t('UserPlanView.points_used') }}</th>
                    <th scope="col">{{ $t('UserPlanView.fee') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>0～2500</td>
                    <td>¥67,500（税別） / 月</td>
                </tr>
                <tr>
                    <td>2501～5000</td>
                    <td>¥80,000（税別） / 月</td>
                </tr>
                <tr>
                    <td>5001～10000</td>
                    <td>¥105,000（税別） / 月</td>
                </tr>
                <tr>
                    <td>10001～20000</td>
                    <td>¥145,000（税別） / 月</td>
                </tr>
                <tr>
                    <td>20001～30000</td>
                    <td>¥175,000（税別） / 月</td>
                </tr>
            </tbody>
        </table>
    </div>

</template>
<script setup>
import { ref, reactive, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { adminApi } from "@/api";
import LoadingIcon from "@/components/LoadingIcon.vue";

const router = useRouter();
const monthList = ref([]);
const selectedMonth = ref("");
const MonthPoint = reactive({});
const ResCount = reactive({});
const isLoading = ref(false);

onMounted(async () => {
    await checkAuthorization();
    monthList.value = await adminApi.getMonthList();
    // 年月を時系列順（最新が上）にソート
    monthList.value.sort((a, b) => new Date(b) - new Date(a));
    console.log('Month list:', monthList.value);
    
    // 初期月を設定し、その月のデータを取得
    if (monthList.value.length > 0) {
        selectedMonth.value = monthList.value[0];
        console.log('Initial selected month:', selectedMonth.value);
        await getMonthPoints(selectedMonth.value);
    }
    
    const resourcesCount = await adminApi.getResourcesCount();
    Object.assign(ResCount, resourcesCount);
});

watch(selectedMonth, async (newMonth, oldMonth) => {
    console.log('Month changed from', oldMonth, 'to', newMonth);
    if (!newMonth) return; // 月が選択されていない場合は何もしない
    
    isLoading.value = true;
    try {
        await getMonthPoints(newMonth);
    } catch (error) {
        console.error('Error fetching month data:', error);
    } finally {
        isLoading.value = false;
    }
}, { immediate: false })

async function getMonthPoints(month) {
    try {
        console.log('Fetching month points for month:', month);
        const monthPoints = await adminApi.getMonthPoints(month);
        console.log('Month points response:', monthPoints);
        Object.assign(MonthPoint, monthPoints);
        console.log('Updated MonthPoint:', MonthPoint);
    } catch (error) {
        console.error('Error fetching month points:', error);
        // エラー時はデフォルト値を設定
        Object.assign(MonthPoint, {
            models: [],
            messages_total: 0,
            total_points: 0,
            total_amount: "0"
        });
    }
}

async function checkAuthorization() {
    // // 権限チェック
    // try {
    //     const authResp = await adminApi.check_authorization();
    //     console.log(authResp);
    //     if (authResp.message !== "ok") {
    //         // 認可が"ok"でない場合、エラーページへリダイレクト
    //         return router.push("../../admin_error");
    //     }
    // } catch (error) {
    //     console.error(error.message);
    //     return router.push("../../admin_error");
    //     // ここでエラーページにリダイレクトするか、エラーメッセージを表示する処理を追加
    // }
}

const handleReturn = () => {
    router.back();
};

const downloadCSV = async () => {
    isLoading.value = true;
    try {
        const csvData = await adminApi.getMonthPointsCSV(selectedMonth.value);
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `月間ポイント_${selectedMonth.value}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.error("CSVダウンロードエラー:", error);
    } finally {
        isLoading.value = false;
    }
};

const downloadCSVByUser = async () => {
    isLoading.value = true;
    try {
        const csvData = await adminApi.getMonthPointsCSVByUser(selectedMonth.value);
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `月間ポイント_ユーザー別_${selectedMonth.value}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.error("ユーザー別CSVダウンロードエラー:", error);
    } finally {
        isLoading.value = false;
    }
};

function formatDateString(dateString) {
    let date = new Date(dateString)
    let y = date.getFullYear();
    let M = date.getMonth() + 1;
    return y + "年" + M + "月"
}

</script>
<style scoped></style>